import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button.jsx';
import FormField from '../../components/ui/FormField.jsx';
import AdminAuthShell, { adminAuthInputLight } from '../../components/admin/AdminAuthShell.jsx';
import { useAuthStore } from '../../store/authStore.js';
import { toastError, toastWarning } from '../../hooks/useToast.js';
import SumCaptcha from '../../components/auth/SumCaptcha.jsx';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const token = useAuthStore((s) => s.token);

  const from = location.state?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    let raw;
    try {
      raw = sessionStorage.getItem('admin_auth_notice');
    } catch {
      return;
    }
    if (!raw) return;
    try {
      sessionStorage.removeItem('admin_auth_notice');
    } catch {
      /* ignore */
    }
    try {
      const parsed = JSON.parse(raw);
      const title =
        typeof parsed?.title === 'string' && parsed.title.trim() !== ''
          ? parsed.title.trim()
          : 'Sesión no válida';
      const text =
        typeof parsed?.text === 'string' && parsed.text.trim() !== ''
          ? parsed.text
          : 'Vuelve a iniciar sesión para continuar en el panel.';
      toastWarning(title, text);
    } catch {
      toastWarning('Sesión no válida', 'Vuelve a iniciar sesión para continuar en el panel.');
    }
  }, []);

  useEffect(() => {
    if (token) navigate(from, { replace: true });
  }, [token, from, navigate]);

  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values) {
    if (!captchaValid) {
      toastError('Verificación requerida', 'Selecciona el resultado correcto de la suma.');
      return;
    }
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (e) {
      toastError('No se pudo iniciar sesión', e.message);
      setCaptchaAnswer('');
      setCaptchaValid(false);
    }
  }

  const loading = useAuthStore((s) => s.loading);

  return (
    <AdminAuthShell
      title="Acceso administrativo"
      subtitle="Gestión de contenidos del portal de empleados"
    >
      <form
        className="space-y-5 [&_label>span]:text-slate-600 [&_p.text-red-400]:text-red-600"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <FormField label="Correo" error={errors.email?.message}>
          <input
            autoComplete="email"
            placeholder="Correo electrónico"
            className={adminAuthInputLight}
            {...register('email', { required: 'Requerido' })}
          />
        </FormField>
        <FormField label="Contraseña" error={errors.password?.message}>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Contraseña de acceso"
              className={`${adminAuthInputLight} pr-11`}
              {...register('password', { required: 'Requerido' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition hover:text-slate-600"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              ) : (
                <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              )}
            </button>
          </div>
        </FormField>

        <div className="flex justify-end">
          <Link
            to="/admin/olvido-clave"
            className="text-sm font-medium text-brand-navy hover:text-brand-green hover:underline"
          >
            ¿Olvidó su contraseña?
          </Link>
        </div>

        <SumCaptcha
          value={captchaAnswer}
          onChange={setCaptchaAnswer}
          onValidChange={setCaptchaValid}
        />

        <Button type="submit" disabled={loading || !captchaValid} className="w-full">
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Entrando…
            </>
          ) : (
            'Iniciar sesión'
          )}
        </Button>
      </form>
    </AdminAuthShell>
  );
}
